import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  HStack,
  Text,
  IconButton,
  Flex,
  Tag
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { MdOpenInNew, MdTouchApp, MdBlock } from 'react-icons/md';

const RecipientTable = ({
  recipients,
  totalRecipients,
  currentPage,
  onPageChange,
  filter,
  onFilterChange
}) => {
  const itemsPerPage = 50;
  const totalPages = Math.ceil(totalRecipients / itemsPerPage);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'delivered': { color: 'green', label: 'Entregue' },
      'sent': { color: 'blue', label: 'Enviado' },
      'bounced': { color: 'red', label: 'Rejeitado' },
      'blocked': { color: 'orange', label: 'Bloqueado' },
      'spam_report': { color: 'red', label: 'Spam' }
    };

    const config = statusConfig[status] || { color: 'gray', label: status };
    
    return (
      <Badge colorScheme={config.color} fontSize="xs">
        {config.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      'cpf': { color: 'blue', label: 'CPF' },
      'cnpj': { color: 'purple', label: 'CNPJ' },
      'oab': { color: 'green', label: 'OAB' }
    };

    const config = typeConfig[type] || { color: 'gray', label: type };
    
    return (
      <Tag size="sm" colorScheme={config.color}>
        {config.label}
      </Tag>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      {/* Filter Bar */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={4}>
          <Text fontWeight="medium">Filtrar por:</Text>
          <Select
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            width="200px"
            size="sm"
          >
            <option value="all">Todos</option>
            <option value="engaged">Engajados</option>
            <option value="not_engaged">Não Engajados</option>
            <option value="bounced">Rejeitados</option>
          </Select>
          <Text fontSize="sm" color="gray.600">
            Total: {totalRecipients} destinatários
          </Text>
        </HStack>
        
        {/* Pagination */}
        <HStack spacing={2}>
          <IconButton
            icon={<ChevronLeftIcon />}
            size="sm"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            isDisabled={currentPage === 1}
            aria-label="Página anterior"
          />
          <Text fontSize="sm">
            Página {currentPage} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            isDisabled={currentPage === totalPages}
            aria-label="Próxima página"
          />
        </HStack>
      </Flex>

      {/* Table */}
      <Box overflowX="auto" bg="white" borderRadius="lg" shadow="sm">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Email</Th>
              <Th>Nome</Th>
              <Th>Tipo</Th>
              <Th>Status</Th>
              <Th isNumeric>Aberturas</Th>
              <Th isNumeric>Cliques</Th>
              <Th>Enviado em</Th>
              <Th>Entregue em</Th>
            </Tr>
          </Thead>
          <Tbody>
            {recipients.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text color="gray.500">Nenhum destinatário encontrado</Text>
                </Td>
              </Tr>
            ) : (
              recipients.map((recipient) => (
                <Tr key={recipient._id} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <Text fontSize="sm" fontWeight="medium">
                      {recipient.recipientEmail}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {recipient.recipientName || '-'}
                    </Text>
                  </Td>
                  <Td>
                    {getTypeBadge(recipient.recipientType)}
                  </Td>
                  <Td>
                    {getStatusBadge(recipient.status)}
                    {recipient.unsubscribed && (
                      <Badge ml={1} colorScheme="yellow" fontSize="xs">
                        Descadastrado
                      </Badge>
                    )}
                  </Td>
                  <Td isNumeric>
                    <HStack justify="flex-end" spacing={1}>
                      {recipient.opens?.count > 0 ? (
                        <>
                          <MdOpenInNew size={14} color="#805AD5" />
                          <Text fontSize="sm" color="purple.600" fontWeight="medium">
                            {recipient.opens.count}
                          </Text>
                        </>
                      ) : (
                        <Text fontSize="sm" color="gray.400">0</Text>
                      )}
                    </HStack>
                  </Td>
                  <Td isNumeric>
                    <HStack justify="flex-end" spacing={1}>
                      {recipient.clicks?.count > 0 ? (
                        <>
                          <MdTouchApp size={14} color="#DD6B20" />
                          <Text fontSize="sm" color="orange.600" fontWeight="medium">
                            {recipient.clicks.count}
                          </Text>
                        </>
                      ) : (
                        <Text fontSize="sm" color="gray.400">0</Text>
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.600">
                      {formatDate(recipient.sentAt)}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.600">
                      {formatDate(recipient.deliveredAt)}
                    </Text>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <Flex justify="center" mt={4}>
          <HStack spacing={2}>
            <IconButton
              icon={<ChevronLeftIcon />}
              size="sm"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              isDisabled={currentPage === 1}
              aria-label="Página anterior"
            />
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <IconButton
                  key={pageNum}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
                  variant={currentPage === pageNum ? 'solid' : 'outline'}
                  aria-label={`Página ${pageNum}`}
                >
                  {pageNum}
                </IconButton>
              );
            })}
            <IconButton
              icon={<ChevronRightIcon />}
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              isDisabled={currentPage === totalPages}
              aria-label="Próxima página"
            />
          </HStack>
        </Flex>
      )}
    </Box>
  );
};

export default RecipientTable;